import { useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { Client } from '../types/client';
import { BsBuilding, BsThreeDotsVertical } from 'react-icons/bs';
import { format } from 'date-fns';
import { CreateDropdown } from '../components/CreateDropdown';
import { NewProjectForm } from '../components/NewProjectForm';
import { NotificationButton } from '../components/NotificationButton';

// Temporary sample data
const sampleClients: Client[] = [
  {
    id: '1',
    companyName: 'EcoTech Motors',
    accountManager: {
      id: '1',
      name: 'John Smith',
      email: 'john.smith@mirapath.com'
    },
    clientManager: {
      name: 'Sarah Johnson',
      email: 'sarah.j@tesla.com',
      phone: '+1 (555) 123-4567'
    },
    firstContractDate: '2023-01-15',
    projects: [
      { id: '1', name: 'Factory Automation', status: 'IN_PROGRESS' },
      { id: '2', name: 'Data Center Expansion', status: 'COMPLETED' }
    ],
    contacts: [
      {
        id: '1',
        name: 'Mike Anderson',
        position: 'Facility Manager',
        email: 'mike.a@tesla.com',
        phone: '+1 (555) 234-5678'
      }
    ],
    address: '3500 Deer Creek Road, Palo Alto, CA 94304',
    phone: '+1 (888) 518-3752',
    email: 'contact@ecotechmotors.com'
  },
  {
    id: '2',
    companyName: 'TechCorp Solutions',
    accountManager: {
      id: '2',
      name: 'Emily Davis',
      email: 'emily.davis@mirapath.com'
    },
    clientManager: {
      name: 'Tom Wilson',
      email: 't.wilson@techcorp.com',
      phone: '+1 (555) 987-6543'
    },
    firstContractDate: '2023-03-20',
    projects: [
      { id: '3', name: 'Corporate Campus Infrastructure', status: 'IN_PROGRESS' }
    ],
    contacts: [
      {
        id: '2',
        name: 'Lisa Brown',
        position: 'Project Coordinator',
        email: 'l.brown@techcorp.com',
        phone: '+1 (555) 876-5432'
      }
    ],
    address: '123 Innovation Drive, Cupertino, CA 95014',
    phone: '+1 (555) 275-2273',
    email: 'contact@techcorp.com'
  }
];

const Clients = () => {
  const navigate = useNavigate();
  const [clients] = useState<Client[]>(sampleClients);
  const [selectedClient, setSelectedClient] = useState<string | null>(null);
  const [showNewProject, setShowNewProject] = useState<boolean>(false);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'COMPLETED':
        return 'bg-green-100 text-green-800';
      case 'IN_PROGRESS':
        return 'bg-primary/10 text-primary';
      default:
        return 'bg-[#f5f5f5] text-[#171717]';
    }
  };

  return (
    <div className="flex flex-col w-full h-full p-6">
      {showNewProject && (
        <NewProjectForm 
          onClose={() => setShowNewProject(false)} 
          onProjectCreated={async (projectId: string) => {
            // Set active tab to team for the newly created project
            localStorage.setItem('activeProjectTab', 'team');
            // Navigate to the new project's Team tab
            navigate(`/projects/${projectId}`);
          }}
        />
      )}
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-semibold">Clients</h1>
        <div className="flex items-center gap-4">
          <CreateDropdown onSelectProject={() => setShowNewProject(true)} />
          <NotificationButton />
        </div>
      </div>
      <div className="flex-1 bg-white rounded-xl shadow-[0_1px_2px_rgba(0,0,0,0.04)] overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-[#eaeaea]">
            <thead className="bg-[#fafafa]">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-[#888] uppercase tracking-wider">
                  Company
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-[#888] uppercase tracking-wider">
                  Account Manager
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-[#888] uppercase tracking-wider">
                  Client Manager
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-[#888] uppercase tracking-wider">
                  First Contract
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-[#888] uppercase tracking-wider">
                  Projects
                </th>
                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-[#888] uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-[#eaeaea]">
              {clients.map((client) => (
                <tr 
                  key={client.id} 
                  className="hover:bg-[#fafafa]"
                  onClick={() => setSelectedClient(selectedClient === client.id ? null : client.id)}
                >
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-10 w-10 bg-[#f5f5f5] rounded-lg flex items-center justify-center">
                        <BsBuilding className="h-5 w-5 text-[#888]" />
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-[#171717]">{client.companyName}</div>
                        <div className="text-sm text-[#888]">{client.email}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-[#171717]">{client.accountManager.name}</div>
                    <div className="text-sm text-[#888]">{client.accountManager.email}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-[#171717]">{client.clientManager.name}</div>
                    <div className="text-sm text-[#888]">{client.clientManager.email}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-[#888]">
                    {format(new Date(client.firstContractDate), 'MMM d, yyyy')}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex flex-col gap-1">
                      {client.projects.map(project => (
                        <div key={project.id} className="flex items-center gap-2">
                          <Link 
                            to={`/projects/${project.id}`}
                            className="text-sm text-[#171717] hover:text-primary"
                          >
                            {project.name}
                          </Link>
                          <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(project.status)}`}>
                            {project.status.replace('_', ' ')}
                          </span>
                        </div>
                      ))}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <button className="text-[#aaa] hover:text-[#555]">
                      <BsThreeDotsVertical className="w-5 h-5" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Clients; 